import { adviseQualityAlert, deliveryTarget } from './qualityAlertAdvice';
import { nelPlainText, nelWordHtml } from './nelQualityAlertDocument';
import { nclPlainText, nclWordHtml } from './nclQualityAlertDocument';

const IDENTITY_LABELS = {
  completedOn: 'Date',
  nhsNumber: 'NHS number',
  patientName: 'Patient name',
  dateOfBirth: 'Date of birth',
  practiceName: 'GP practice',
  raiserName: "GP's name",
  practicePhone: 'Practice telephone',
  practiceEmail: 'Generic practice email',
  gmcNumber: 'GP GMC number',
  odsCode: 'Practice code',
  reporterName: 'Reporter name (if not the GP)',
};

function singleLines(options, groups, value) {
  const flat = [
    ...(options || []),
    ...(groups || []).flatMap((group) => (group.options || []).map((option) => ({ ...option, group: group.label }))),
  ];
  const id = value?.id;
  return flat.map((option) => `${option.id === id ? '☑' : '☐'} ${option.group ? `${option.group}: ` : ''}${option.label}${option.id === 'other' && id === 'other' && value?.other ? ` — ${value.other}` : ''}`);
}

function multiLines(groups, value) {
  const ids = new Set(value?.ids || []);
  return (groups || []).flatMap((group) => (group.options || []).map((option) => (
    `${ids.has(option.id) ? '☑' : '☐'} ${group.label}: ${option.label}${option.id === 'other' && ids.has('other') && value?.other ? ` — ${value.other}` : ''}`
  )));
}

function identityLines(fields, context) {
  return (fields || []).map((key) => `${IDENTITY_LABELS[key] || key}: ${context?.[key] || ''}`);
}

export function qualityAlertLines(template, fields, context) {
  if (template.type === 'NEL') return nelPlainText(template, fields, context).split('\n');
  if (template.type === 'NCL') return nclPlainText(template, fields, context).split('\n');
  const lines = [template.displayName, template.icbName, ''];
  if (template.type === 'NCL') {
    lines.push(template.intro, '', ...identityLines(template.identityFields, context), '', 'Provider');
    lines.push(...singleLines(null, template.providers, fields.provider), '', 'Specialty / service', fields.specialty || '', '');
    lines.push('Nature of concern', ...multiLines(template.concerns, fields.concerns), '', 'Details of concern', fields.details || '', '');
    lines.push('Action', ...singleLines(template.actions, null, fields.action), '');
    lines.push('Submission', ...singleLines(template.submissions, null, fields.submission), '');
  } else {
    lines.push(template.intro, '', 'Provider', ...singleLines(template.providers, null, fields.provider), '');
    lines.push('Urgency', ...singleLines(template.urgencyOptions, null, fields.urgency), '');
    lines.push('Borough', ...singleLines(template.boroughs, null, fields.borough), '');
    lines.push(...identityLines(template.identityFields, context), '', 'Quality issue', fields.narrative || '', '', template.sla, '');
  }
  const advice = adviseQualityAlert(template, fields, context?.nhsNumber);
  if (advice) lines.push(advice, '');
  return lines;
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function downloadQualityAlert(template, fields, context) {
  const nhs = (context?.nhsNumber || 'unknown').replace(/\s+/g, '');
  const format = template.format === 'docx' ? 'docx' : 'html';
  if (template.type === 'NEL' || template.type === 'NCL') {
    const html = template.type === 'NEL' ? nelWordHtml(template, fields, context) : nclWordHtml(template, fields, context);
    const filename = `quality-alert-${nhs}.doc`;
    downloadBlob(new Blob([html], { type: 'application/msword' }), filename);
    return filename;
  }
  const filename = `quality-alert-${nhs}.${format}`;
  if (format === 'docx') {
    downloadBlob(buildDocx(qualityAlertLines(template, fields, context)), filename, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
  } else {
    const body = qualityAlertLines(template, fields, context).map((line) => `<p>${escapeHtml(line) || '&nbsp;'}</p>`).join('');
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>${escapeHtml(template.displayName)}</title><style>body{font-family:Georgia,serif;max-width:760px;margin:40px auto;line-height:1.45;color:#111}p{margin:0 0 6px}</style></head><body>${body}</body></html>`;
    downloadBlob(new Blob([html], { type: 'text/html' }), filename, 'text/html');
  }
  return filename;
}

export function emailQualityAlert(template, fields, context) {
  const target = deliveryTarget(template, fields);
  if (!target.email) return false;
  downloadQualityAlert(template, fields, context);
  const nhs = context?.nhsNumber || '';
  const subject = (template.subject || 'Quality alert {{nhsNumber}}').replaceAll('{{nhsNumber}}', nhs);
  const body = adviseQualityAlert(template, fields, nhs);
  window.location.href = `mailto:${target.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return true;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildDocx(lines) {
  const paragraphs = lines.map((line) => {
    const text = escapeXml(line);
    return `<w:p><w:r><w:t xml:space="preserve">${text}</w:t></w:r></w:p>`;
  }).join('');
  const documentXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"><w:body>${paragraphs}<w:sectPr/></w:body></w:document>`;
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/></Types>`;
  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="word/document.xml"/></Relationships>`;
  return zipStore([
    ['[Content_Types].xml', contentTypes],
    ['_rels/.rels', rels],
    ['word/document.xml', documentXml],
  ]);
}

function escapeXml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function zipStore(files) {
  const encoder = new TextEncoder();
  const parts = [];
  const central = [];
  let offset = 0;
  for (const [name, text] of files) {
    const nameBytes = encoder.encode(name);
    const data = encoder.encode(text);
    const crc = crc32(data);
    const local = new Uint8Array(30 + nameBytes.length + data.length);
    const view = new DataView(local.buffer);
    view.setUint32(0, 0x04034b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(8, 0, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, data.length, true);
    view.setUint32(22, data.length, true);
    view.setUint16(26, nameBytes.length, true);
    local.set(nameBytes, 30);
    local.set(data, 30 + nameBytes.length);
    parts.push(local);
    const header = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(header.buffer);
    centralView.setUint32(0, 0x02014b50, true);
    centralView.setUint16(4, 20, true);
    centralView.setUint16(6, 20, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, data.length, true);
    centralView.setUint32(24, data.length, true);
    centralView.setUint16(28, nameBytes.length, true);
    centralView.setUint32(42, offset, true);
    header.set(nameBytes, 46);
    central.push(header);
    offset += local.length;
  }
  const centralOffset = offset;
  const centralSize = central.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  endView.setUint32(0, 0x06054b50, true);
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralSize, true);
  endView.setUint32(16, centralOffset, true);
  const blobParts = [...parts, ...central, end];
  return new Blob(blobParts, { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
}

let crcTable;
function crc32(bytes) {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n += 1) {
      let c = n;
      for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const byte of bytes) crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}
